import {createStyles, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {GetTableDataFn} from './extractTableDataForSuggestions';
import {getIconForChartType, PlotSuggestion} from './SuggestPlotsConstants';
import {fadeInKeyframes, LoadingShimmerList, spinKeyframes} from './SuggestPlotsLoadingComponents';
import useSuggestPlots from './useSuggestPlots';

export interface SuggestedPlotsPanelProps {
  entryId: string;
  tableName: string;
  getTableData: GetTableDataFn;
  onClose?: () => void;
  autoFetch?: boolean;
}

export default function SuggestedPlotsPanel({
  entryId,
  tableName,
  getTableData,
  onClose,
  autoFetch = false,
}: SuggestedPlotsPanelProps): JSX.Element {
  const {classes} = useStyles();
  const {
    isLoading,
    suggestions,
    errorMessage,
    launchingSuggestionKey,
    fetchSuggestions,
    handleSuggestionClick,
    handleCustomClick,
  } = useSuggestPlots({entryId, tableName, getTableData});

  // Mount-only: kick off a fetch if prefetch hasn't already populated suggestions.
  useOnMount(() => {
    if (autoFetch && suggestions.length === 0 && !isLoading && errorMessage == null) {
      fetchSuggestions().catch(() => undefined);
    }
  });

  const isLaunchingChat = launchingSuggestionKey != null;
  const isCustomLaunching = launchingSuggestionKey === 'custom';

  const onSuggestionClick = (suggestion: PlotSuggestion): void => {
    trackEvent(ELN_ANALYTICS_ACTIONS.CLICKED_AI_SUGGESTED_PLOTS_OPTION, {
      actionOrigin: ActionOrigin.NOTEBOOK,
      selectedOptionType: suggestion.chartType,
    });
    handleSuggestionClick(suggestion);
    onClose?.();
  };

  const onCustomClickWrapped = (): void => {
    trackEvent(ELN_ANALYTICS_ACTIONS.CLICKED_AI_SUGGESTED_PLOTS_OPTION, {
      actionOrigin: ActionOrigin.NOTEBOOK,
      selectedOptionType: 'custom',
    });
    handleCustomClick();
    onClose?.();
  };

  return (
    <div className={classes.root}>
      <Text size="xs" weight={600} color="dimmed" className={classes.sectionLabel}>
        Suggested plots
      </Text>
      <Stack spacing="xs">
        {isLoading ? (
          <LoadingShimmerList />
        ) : errorMessage != null ? (
          <Text size="xs" color="orange" className={classes.plotError}>
            {errorMessage}
          </Text>
        ) : (
          suggestions.map((suggestion, idx) => {
            const key = `${idx}-${suggestion.title}`;
            const isLaunching = isLaunchingChat && launchingSuggestionKey === key;
            return (
              <UnstyledButton
                key={key}
                className={classes.plotSuggestion}
                onClick={() => {
                  onSuggestionClick(suggestion);
                }}
                disabled={isLaunchingChat}
                style={{animationDelay: `${idx * 60}ms`}}
              >
                <div className={classes.plotSuggestionIcon}>
                  <BenchlingIcon icon={getIconForChartType(suggestion.chartType)} />
                </div>
                <div className={classes.plotSuggestionText}>
                  <Text size="sm" weight={600} lineClamp={1}>
                    {suggestion.title}
                  </Text>
                  <Text size="xs" color="dimmed" lineClamp={1}>
                    {suggestion.description}
                  </Text>
                </div>
                <div className={classes.chevron}>
                  <BenchlingIcon
                    icon={isLaunching ? 'spinner-third' : 'arrow-right'}
                    className={isLaunching ? classes.spin : undefined}
                  />
                </div>
              </UnstyledButton>
            );
          })
        )}
        <UnstyledButton
          className={classes.plotSuggestion}
          onClick={onCustomClickWrapped}
          disabled={isLaunchingChat}
          style={{animationDelay: `${suggestions.length * 60}ms`}}
        >
          <div className={classes.plotSuggestionIcon}>
            <BenchlingIcon icon="chart-line" />
          </div>
          <div className={classes.plotSuggestionText}>
            <Text size="sm" weight={600}>
              Custom
            </Text>
            <Text size="xs" color="dimmed" lineClamp={1}>
              Let AI help you customize
            </Text>
          </div>
          <div className={classes.chevron}>
            <BenchlingIcon
              icon={isCustomLaunching ? 'spinner-third' : 'arrow-right'}
              className={isCustomLaunching ? classes.spin : undefined}
            />
          </div>
        </UnstyledButton>
      </Stack>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    width: 340,
    padding: theme.spacing.xs,
  },
  sectionLabel: {
    paddingLeft: theme.spacing.sm,
    paddingTop: theme.spacing.xs,
  },
  plotError: {
    padding: theme.spacing.sm,
  },
  plotSuggestion: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    borderRadius: theme.radius.sm,
    height: 48,
    transition: 'background-color 140ms ease, transform 140ms ease',
    opacity: 0,
    animation: `${fadeInKeyframes} 260ms ease forwards`,
    '&:hover:not(:disabled)': {
      backgroundColor: theme.colors.violet[0],
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  plotSuggestionIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: theme.radius.sm,
    flexShrink: 0,
    backgroundColor: theme.colors.violet[0],
    color: theme.colors.violet[7],
    fontSize: theme.fontSizes.sm,
  },
  plotSuggestionText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  chevron: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.gray[6],
    paddingTop: 2,
  },
  spin: {
    animation: `${spinKeyframes} 1s linear infinite`,
    display: 'inline-block',
  },
}));
